import { motion } from "framer-motion";
import {
  DollarSign,
  Eye,
  AlignJustify,
  PercentCircle,
  MousePointerClick,
  Percent,
  Smartphone,
  PiIcon,
  User2,
  CalendarDays,
} from "lucide-react";
import { FaUserGroup } from "react-icons/fa6";
import { useEffect, useState, useCallback } from "react";
import propertyService from "../../Services/propertyService";

export default function WebAnalyticsFormModal({
  modalOpen,
  handleSubmit,
  editItem,
  closeModal,
  publishers = [],
  adunits = []
}) {
  const [selectedPublisher, setSelectedPublisher] = useState(editItem?.publisherId || "");
  const [webOptions, setWebOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch websites when publisher changes
  const fetchWebsites = useCallback(async () => {

    if (!selectedPublisher) {
      setWebOptions([]);
      return;
    }

    setIsLoading(true);
    try {
      const result = await propertyService.getWebsitesByPublisher(selectedPublisher);
      setWebOptions(result);
    } catch (error) {
      console.error("Failed to fetch websites", error);
      setWebOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedPublisher]);

  useEffect(() => {
    fetchWebsites();
  }, [fetchWebsites]);

  // Reset form when modal closes
  useEffect(() => {
    if (!modalOpen) {
      setSelectedPublisher("");
      setWebOptions([]);
    }
  }, [modalOpen]);

  // Animation variants
  const modalVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 }
  };

  const formFields = [
    {
      name: "publisher",
      custom: (
        <div className="relative group">
          <FaUserGroup className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 pointer-events-none" />
          <select
            name="publisher"
            value={selectedPublisher}
            onChange={(e) => setSelectedPublisher(e.target.value)}
            required
            className="appearance-none pl-10 pr-10 py-2.5 border rounded-lg w-full bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500"
            disabled={isLoading}
          >
            <option value="" disabled>Select Publisher</option>
            {publishers.map((pub) => (
              <option key={pub.id} value={pub.id}>{pub.name}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            ▼
          </div>
        </div>
      ),
    },
    {
      name: "propertyId",
      custom: (
        <div className="relative group">
          <Smartphone className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 pointer-events-none" />
          <select
            name="propertyId"
            defaultValue={editItem?.propertyId || ""}
            required
            disabled={!selectedPublisher || isLoading}
            className="appearance-none pl-10 pr-10 py-2.5 border rounded-lg w-full bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">Select Website</option>
            {webOptions.map((web) => (
              <option key={web.id} value={web.id}>
                {web.name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            ▼
          </div>
        </div>
      ),
    },
    {
      name: "adunit",
      custom: (
        <div className="relative group">
          <PiIcon className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 pointer-events-none" />
          <select
            name="adunit"
            defaultValue={editItem?.adunit || ""}
            required
            className="appearance-none pl-10 pr-10 py-2.5 border rounded-lg w-full bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500"
          >
            <option value="" disabled>Select Ad Unit</option>
            {adunits.map((adunit) => (
              <option key={adunit.name} value={adunit.name}>
                {adunit.name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            ▼
          </div>
        </div>
      ),
    },
    {
      icon: DollarSign,
      name: "revenue",
      placeholder: "Revenue",
      type: "number",
      step: "0.01"
    },
    {
      icon: Eye,
      name: "impressions",
      placeholder: "Ad Impressions",
      type: "number",
      step: "0.01"
    },
    {
      icon: AlignJustify,
      name: "adRequests",
      placeholder: "Ad Requests",
      type: "number",
      step: "0.01"
    },
    {
      icon: PercentCircle,
      name: "viewability",
      placeholder: "Viewability (%)",
      type: "number",
      step: "0.01"
    },
    {
      icon: MousePointerClick,
      name: "clicks",
      placeholder: "Clicks",
      type: "number"
    },
    {
      icon: DollarSign,
      name: "ecpm",
      placeholder: "eCPM",
      type: "number",
      step: "0.01"
    },
    {
      icon: Percent,
      name: "fillRate",
      placeholder: "Fill Rate (%)",
      type: "number",
      step: "0.01"
    },
    {
      icon: Percent,
      name: "ctr",
      placeholder: "CTR (%)",
      type: "number",
      step: "0.01"
    },
    {
      name: "date",
      custom: (
        <div className="relative group">
          <CalendarDays className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500 pointer-events-none" />
          <input
            name="date"
            type="date"
            defaultValue={editItem?.date ? formatDateForInput(editItem.date) : ""}
            required
            className="pl-10 pr-4 py-2.5 border rounded-lg w-full text-gray-700 bg-white focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      ),
    },
  ];

  if (!modalOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="bg-white rounded-xl p-6 w-full max-w-4xl shadow-xl overflow-y-auto max-h-[90vh]"
      >
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <h2 className="md:col-span-2 text-xl font-bold text-gray-800">
            {editItem ? "Update Web Analytics" : "Add Web Analytics"}
          </h2>

          {formFields.map((field, index) => (
            <motion.div
              key={field.name || index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="relative"
            >
              {field.custom ? (
                field.custom
              ) : (
                <div className="relative group">
                  <field.icon className="absolute left-3 top-3 text-gray-400 group-focus-within:text-indigo-500" />
                  <input
                    name={field.name}
                    type={field.type}
                    placeholder={field.placeholder}
                    defaultValue={
                      editItem?.[field.name] !== undefined
                        ? Number(editItem[field.name]).toFixed(2)
                        : ""
                    }
                    className="pl-10 pr-4 py-2.5 border rounded-lg w-full focus:ring-2 focus:ring-indigo-500"
                    required
                    min={field.min}
                    max={field.max}
                    step={field.step}
                  />
                </div>
              )}
            </motion.div>
          ))}

          <div className="md:col-span-2 flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              {editItem ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Helper function to format date for input[type="date"]
function formatDateForInput(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
}